import React, { useEffect } from 'react';
import { Form, Select, FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import flagImages from 'utils/flagImages';
import { fieldDefaultProps } from 'utils/fieldConfig';
import { useCountries } from 'app/hooks/useCountries';

interface CountryInputProps {
  form: FormInstance;
  name: string;
  dataTestId?: string;
  label?: string;
  className?: string;
  required?: boolean;
  requiredMessage?: string;
  disabled?: boolean;
  requiredName?: boolean; // flag for sending name to the API instead of id
}

const CountryInput: React.FC<CountryInputProps> = ({
  form,
  name,
  dataTestId,
  label,
  className,
  required = true,
  requiredMessage,
  disabled = false,
  requiredName = false, // flag for sending name to the API instead of id
}) => {
  const { t } = useTranslation();
  const { data } = useCountries();

  const countryNameMap: Record<string | number, string> =
    data?.data?.reduce((map, country) => ({ ...map, [country.id]: country.name }), {}) ?? {};

  const countryIdByNameMap: Record<string, number> =
    data?.data?.reduce((map, country) => ({ ...map, [country.name.toLowerCase()]: country.id }), {}) ?? {};

  useEffect(() => {
    const currentValue = form.getFieldValue(name);
    if (currentValue && typeof currentValue === 'string') {
      const countryId = countryIdByNameMap[currentValue.toLowerCase()];
      if (countryId) {
        form.setFieldsValue({ [name]: requiredName ? currentValue : countryId });
      } else {
        form.setFields([
          {
            name,
            errors: [t('common.errors.country_not_found')],
          },
        ]);
      }
    }
  }, [data?.data, form, name, t, requiredName]);

  const handleCountryID = (value: number) => {
    if (requiredName) {
      const countryName = countryNameMap[value];
      form.setFieldsValue({ [name]: countryName });
    } else {
      form.setFieldsValue({ [name]: value });
    }
  };

  const validateCountry = (_: any, value: any) => {
    if (!value && required) {
      return Promise.reject(new Error(requiredMessage ?? t('auth.signup.rules.country_required')));
    }

    if (typeof value === 'string') {
      const countryId = countryIdByNameMap[value.toLowerCase()];
      if (!countryId) {
        return Promise.reject(new Error(t('common.errors.country_not_found')));
      }
    }

    return Promise.resolve();
  };

  return (
    <Form.Item
      label={label ?? t('auth.signup.country')}
      name={name}
      rules={[
        {
          required: required,
          validator: validateCountry,
        },
      ]}
      className={className}
    >
      <Select
        {...fieldDefaultProps}
        placeholder={t('auth.signup.rules.country_required')}
        data-testid={dataTestId}
        onChange={handleCountryID}
        showSearch
        disabled={disabled}
        allowClear
        filterOption={(input, option) =>
          countryNameMap &&
          option?.value != null &&
          countryNameMap[option?.value as string | number].toLowerCase().includes(input.toLowerCase())
        }
      >
        {data?.data?.map((country) => (
          <Select.Option key={country.id} value={country.id} data-testid={`country-option-${country.id}`}>
            <span className="flex items-center gap-2">
              <img src={flagImages[country?.isoAlpha2]} alt={country.name} className="w-4 h-3" />
              {country.name}
            </span>
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export default CountryInput;
